import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui";
import { type JobRun, type LogMessage, useJobRunLogs } from "./hooks";
import { type LogItem, LogViewer } from "./log-viewer";

interface LogsContainerProps {
  jobRun: JobRun;
  dryRun?: boolean;
  onFetchResult?: () => void;
  /** Lets the caller gate its own affordances on the job reaching [SUCCEEDED]. */
  onJobDoneChange?: (done: boolean) => void;
}

const fill = (val: number, len: number) => val.toString().padStart(len, "0");

const formatTime = (tm: Date) => `${fill(tm.getHours(), 2)}:${fill(tm.getMinutes(), 2)}:${fill(tm.getSeconds(), 2)}`;

const formatLog = (m: LogMessage): LogItem => {
  const tm = m.time ? new Date(m.time) : null;
  const time = tm ? formatTime(tm) : "";
  return { severity: m.event, message: m.message, time };
};

const jobDoneInSuccess = (data: LogMessage[]) => data.some((m) => m.message.includes("[SUCCEEDED]"));

export function LogsContainer({ jobRun, dryRun = false, onFetchResult, onJobDoneChange }: LogsContainerProps) {
  const { data, logSource } = useJobRunLogs(jobRun);
  const { t } = useTranslation();

  const jobDone = jobDoneInSuccess(data);

  useEffect(() => {
    onJobDoneChange?.(jobDone);
  }, [jobDone, onJobDoneChange]);

  return (
    <div>
      {dryRun && onFetchResult && (
        <div className="mb-1 flex justify-end">
          <Button type="button" size="sm" onClick={onFetchResult} disabled={!jobDone}>
            {!jobDone && <Loader2 className="animate-spin" />}
            <span>{t("ui.fetchX", { x: t("ui.result") })}</span>
          </Button>
        </div>
      )}
      <LogViewer data={data.map(formatLog)} logSource={logSource} />
    </div>
  );
}
